import { Tour } from '@/types/tours';
import { formatPrice } from '@/utils/formatPrice';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  bookButtonSx,
  bookLinkStyle,
  contentColumnSx,
  imageColumnSx,
  imageLinkStyle,
  imageStyle,
  imageWrapSx,
  mapPreviewAddressSx,
  mapPreviewContentSx,
  mapPreviewDialogPaperSx,
  mapPreviewIframeSx,
  mapPreviewLinkSx,
  pickupLabelSx,
  pickupRowSx,
  pickupValueSx,
  previewBoxSx,
  previewTextSx,
  priceSx,
  readMoreSx,
  sectionSx,
  titleLinkStyle,
  titleSx,
  wrapperSx,
} from './styles';
import Price from '../Price';

interface LargeTourItemProps {
  tour: Tour;
  index: number;
  tourType: string;
}

const SHORT_CONTENT_MAX_CHARS = 620;

const getPickUpLatLng = (tour: Tour): { lat: number; lng: number } | null => {
  const lat = Number(tour.pickUp?.lat);
  const lng = Number(tour.pickUp?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const getPreviewTextFromHtml = (html: string): string => {
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getLowestTourPrice = (tour: Tour): number => {
  const sortedOptions = [...tour.tourPriceOptions].sort(
    (a, b) =>
      (a.tourPricing?.defaultPrice ?? Number.POSITIVE_INFINITY) -
      (b.tourPricing?.defaultPrice ?? Number.POSITIVE_INFINITY),
  );
  const minPriceOption = sortedOptions[0];
  return minPriceOption?.tourPricing?.defaultPrice || 0;
};

export default function LargeTourItem({
  tour,
  index,
  tourType,
}: LargeTourItemProps) {
  const title = tour.title;
  const [displayMinPrice, setDisplayMinPrice] = useState<number>(() => getLowestTourPrice(tour));
  const [isMapPreviewOpen, setIsMapPreviewOpen] = useState<boolean>(false);

  useEffect(() => {
    setDisplayMinPrice(getLowestTourPrice(tour));
  }, [tour]);

  const shortContentText = getPreviewTextFromHtml(tour.shortContent || '');
  const isLongContent = shortContentText.length > SHORT_CONTENT_MAX_CHARS;
  const previewContent = isLongContent
    ? `${shortContentText.slice(0, SHORT_CONTENT_MAX_CHARS).trim()}...`
    : shortContentText;
  const pickUpLatLng = useMemo(() => getPickUpLatLng(tour), [tour]);
  const canPreviewPickUpOnMap = Boolean(pickUpLatLng);
  const mapQuery = pickUpLatLng ? `${pickUpLatLng.lat},${pickUpLatLng.lng}` : '';
  const mapEmbedUrl = mapQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`
    : '';
  const mapLargerUrl = mapQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}`
    : '';

  return (
    <Box component="section" sx={sectionSx(index)}>
      <Box sx={wrapperSx}>
        <Box sx={imageColumnSx}>
          <Link
            href={`/${tourType}/${tour.slug}`}
            style={imageLinkStyle}
          >
            <Box sx={imageWrapSx}>
              <Image
                src={tour.images[0]}
                alt={title}
                fill
                sizes="(max-width: 900px) 100vw, 500px"
                style={imageStyle}
              />
            </Box>
          </Link>
        </Box>

        <Box sx={contentColumnSx}>
          <Link
            href={`/${tourType}/${tour.slug}`}
            style={titleLinkStyle}
          >
            <Typography component="h3" sx={titleSx}>
              {title}
            </Typography>
          </Link>

          <Typography sx={priceSx}>
            <span>
              <Trans i18nKey="hoiAntour.priceFrom" defaults="From" />
            </span>
            <Price price={displayMinPrice} />
            <Trans i18nKey="hoiAntour.perTour" defaults="/ Tour" />
          </Typography>

          <Box sx={pickupRowSx}>
            <Typography
              component="span"
              sx={pickupLabelSx}
            >
              <Trans i18nKey="hoiAntour.pickUpPoint" defaults="Pick up point:" />
            </Typography>
            {tour.pickUpAddress && (
              <Typography
                component="span"
                sx={pickupValueSx(canPreviewPickUpOnMap)}
                onClick={canPreviewPickUpOnMap ? () => setIsMapPreviewOpen(true) : undefined}
              >
                {tour.pickUpAddress}
              </Typography>
            )}
          </Box>

          {previewContent && (
            <Box sx={previewBoxSx}>
              <Typography sx={previewTextSx}>
                {previewContent}
              </Typography>

              {isLongContent && (
                <Box
                  component={Link}
                  href={`/${tourType}/${tour.slug}`}
                  sx={readMoreSx}
                >
                  <Trans i18nKey="hoiAntour.readMore" defaults="Read more" />
                </Box>
              )}
            </Box>
          )}

          <Link
            href={`/${tourType}/${tour.slug}`}
            style={bookLinkStyle}
          >
            <Button variant="contained" sx={bookButtonSx}>
              <Trans i18nKey="hoiAntour.bookNow" defaults="Book Now" />
            </Button>
          </Link>
        </Box>
      </Box>
      <Dialog
        open={isMapPreviewOpen}
        onClose={() => setIsMapPreviewOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: mapPreviewDialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          Map Preview
          <IconButton
            aria-label="Close map preview"
            onClick={() => setIsMapPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={mapPreviewContentSx}>
          {mapEmbedUrl && (
            <Box
              component="iframe"
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              sx={mapPreviewIframeSx}
            />
          )}
          {tour.pickUpAddress && (
            <Typography sx={mapPreviewAddressSx}>
              {tour.pickUpAddress}
            </Typography>
          )}
          {mapLargerUrl && (
            <Box
              component="a"
              href={mapLargerUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={mapPreviewLinkSx}
            >
              View larger map
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
